import React, { useState, useEffect, useRef } from 'react';
import {
  View, Text, TouchableOpacity, Pressable, StyleSheet, TextInput,
  SafeAreaView, ActivityIndicator, Alert, ScrollView, Platform,
  Linking, KeyboardAvoidingView, Image,
} from 'react-native';
import { useNavigation, StackActions } from '@react-navigation/native';
import * as WebBrowser from 'expo-web-browser';
import * as AuthSession from 'expo-auth-session';
import Constants from 'expo-constants';
import { colors, spacing, radius, shadow } from '../theme';
import { useAuth } from '../context/AuthContext';
import { AWS_CONFIG, IS_AWS_CONFIGURED } from '../config/aws_config';
import { showAppAlert } from '../components/AppDialog';
import { rootReplace, rootResetTo } from '../utils/rootNavigation';
import { useI18n } from '../i18n';
import { openLegalUrl } from '../utils/openLegalUrl';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

WebBrowser.maybeCompleteAuthSession();

/**
 * Détermine le redirectUri à utiliser pour l'OAuth.
 * - En Expo Go SDK 54 : useProxy est déprécié. On utilise makeRedirectUri()
 *   qui retourne exp://<host>:<port>/--/callback (scheme exp géré par Expo Go)
 * - En build native (APK/TestFlight/Play Store) : retourne outfit://callback
 *
 * IMPORTANT : il faut que l'URI retournée soit whitelistée dans Cognito
 * User Pool → App Client → Allowed callback URLs, ET côté Google OAuth :
 *   - outfit://callback             (build native)
 *   - exp://192.168.X.X:8081/--/callback   (Expo Go dev, IP variable)
 */
function getRedirectUri() {
  // v1.12.115 — Support web : redirect HTTPS au lieu de outfit://
  if (Platform.OS === 'web' && typeof window !== 'undefined') {
    return `${window.location.origin}/auth/callback`;
  }
  const isExpoGo = Constants.appOwnership === 'expo';
  if (isExpoGo) {
    // SDK 54 : makeRedirectUri sans useProxy retourne exp://host:port/--/path
    try {
      return AuthSession.makeRedirectUri({
        scheme: 'outfit',
        path: 'callback',
      });
    } catch (e) {
      console.warn('[getRedirectUri] makeRedirectUri failed, fallback', e.message);
      return 'outfit://callback';
    }
  }
  // Build native : scheme outfit://
  return 'outfit://callback';
}

/**
 * LoginScreen
 *
 * Mode démo  (IS_AWS_CONFIGURED = false) : connexion simulée, aucun réseau
 * Mode prod  (IS_AWS_CONFIGURED = true)  : Cognito Hosted UI → Google OAuth
 *
 * Pour activer Google en prod :
 *   1. Configurer Google provider dans Cognito Console
 *   2. Remplir AWS_CONFIG.oauth.domain dans aws_config.js
 *   3. Ajouter outfit://callback dans Google OAuth redirect URIs
 */

export default function LoginScreen() {
  const { t, lang } = useI18n();
  const { signInWithGoogle, signInWithApple, signInWithEmail, loading, isAuthenticated } = useAuth();
  const navigation = useNavigation();
  const route = require('@react-navigation/native').useRoute();
  const [busy, setBusy] = useState(null);
  // v1.12.117 — email + password fields. Prefill possible via route params
  // (depuis ConfirmEmailScreen ou ForgotPasswordScreen).
  const [emailField, setEmailField] = useState(route.params?.prefillEmail || '');
  const [passwordField, setPasswordField] = useState('');
  const [showPwd, setShowPwd] = useState(false);
  // v1.12.118 — état d'erreur inline (champs rouges + message)
  // null | 'invalid' | 'empty' | 'not_confirmed' | 'invalid_email'
  const [loginError, setLoginError] = useState(null);
  // Ref synchrone : setBusy est async (state React) → 2 calls quasi-simultanés
  // passent tous deux le check `if (busy)` avant que le state ne s'update.
  // Avec une ref, l'update est immédiate → garde fiable.
  const submittingRef = useRef(false);
  // v1.12.119 — flag posé à true UNIQUEMENT par un signin réussi → autorise
  // le navigation.goBack() à passer. Tout autre tentative de pop dans la
  // fenêtre critique post-signin est bloquée.
  const allowDismissRef = useRef(false);
  // Timestamp du dernier click "Se connecter". Pendant 1500ms après, on
  // bloque toute auto-dismission du modal pour identifier ce qui fire goBack.
  const lastAttemptRef = useRef(0);

  // Reset l'erreur dès que l'user tape (re-essai)
  const onEmailChange = (v) => { setEmailField(v); if (loginError) setLoginError(null); };
  const onPasswordChange = (v) => { setPasswordField(v); if (loginError) setLoginError(null); };

  // v1.12.119 — validation format email côté client (avant tout appel API)
  const isEmailFormatValid = (e) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((e || '').trim());

  const onEmailLogin = async () => {
    // Garde anti-double-submission synchrone (cf. submittingRef)
    if (submittingRef.current) return;
    submittingRef.current = true;
    lastAttemptRef.current = Date.now();
    try {
      const em = (emailField || '').trim().toLowerCase();
      const pw = passwordField || '';
      // 1) champs vides
      if (!em || !pw) {
        setLoginError('empty');
        return;
      }
      // 2) format email invalide → on N'APPELLE PAS l'API, on reste ici
      if (!isEmailFormatValid(em)) {
        setLoginError('invalid_email');
        return;
      }
      setLoginError(null);
      setBusy('email');
      const r = await signInWithEmail({ email: em, password: pw });
      setBusy(null);
      console.log('[Login] signin result:', { success: r?.success, error: r?.error, needsEmailVerification: !!r?.needsEmailVerification });
      // v1.12.930 — email non vérifié → flow vérification puis retry signin
      if (r?.needsEmailVerification) {
        navigation.navigate('ConfirmEmail', { email: em, flow: 'verify-email' });
        return;
      }
      if (r?.success) {
        // Authentification réussie : on autorise la fermeture puis on
        // déclenche goBack. allowDismissRef bypass le blocage beforeRemove.
        allowDismissRef.current = true;
        if (Platform.OS === 'web') {
          // v1.12.268 — Safari mobile : goBack() ne suffit pas. On force navigation
          // vers / via window.location.replace pour SHIPS le SPA, garantit reload propre.
          setTimeout(() => {
            try {
              // 1) Tente reset complet stack vers Main
              rootResetTo('Main');
            } catch {}
            // 2) Last resort : reload sur racine (assure que App.js re-render avec user authed)
            if (typeof window !== 'undefined') {
              setTimeout(() => {
                try {
                  if (window.location?.pathname && window.location.pathname !== '/') {
                    window.location.replace('/');
                  } else {
                    // Déjà sur /, force un reload pour que App.js refait l'auth gate
                    window.location.reload();
                  }
                } catch {}
              }, 250);
            }
          }, 80);
        } else {
          // v1.12.121 — Reset stack vers Main sur natif
          try {
            navigation.dispatch(StackActions.popToTop());
            setTimeout(() => {
              try { rootReplace('Main'); } catch {}
            }, 50);
          } catch {}
        }
        return;
      }
      const errLower = (r?.error || '').toLowerCase();
      // Compte non vérifié → propose ConfirmEmail
      if (errLower.includes('non vérifié') || errLower.includes('not confirmed')) {
        setLoginError('not_confirmed');
        const goConfirm = () => {
          setTimeout(() => rootReplace('ConfirmEmail', { email: em }), 100);
        };
        showAppAlert(
          t('account_not_confirmed_title'),
          t('account_not_confirmed_msg'),
          [
            { text: t('later_cta'),      style: 'cancel'  },
            { text: t('enter_code_cta'), style: 'default', onPress: goConfirm },
          ],
        );
        return;
      }
      // Identifiants incorrects → erreur inline (champs rouges + message),
      // PAS d'alert popup, on reste sur l'écran login.
      setLoginError('invalid');
    } finally {
      // Délai 600ms avant de relibérer la ref → bloque les éventuels
      // 2e calls retardés (autofill, double Enter, etc.). Plus que la
      // durée typique d'un fetch raté = bloque toute soumission parasite.
      setTimeout(() => { submittingRef.current = false; }, 600);
    }
  };

  // v1.12.118 (Phase 2) — vrai flow reset password via SES
  const onForgotPassword = () => {
    try {
      navigation.navigate('ForgotPassword', { prefillEmail: emailField || '' });
    } catch (e) {
      console.warn('[LoginScreen] navigate ForgotPassword failed:', e?.message);
    }
  };

  // v1.12.117 — handler OAuth Google web déplacé dans App.js (RootNavigator
  // via useWebOAuthCallback) car LoginScreen n'est plus toujours monté en
  // mode browse libre. Voir src/hooks/useWebOAuthCallback.js.

  // v1.12.119 — Bloque toute auto-dismission du modal Login dans la fenêtre
  // critique post-clic "Se connecter" (1500ms). Au-delà, le user peut
  // dismiss manuellement (sidebar, ESC, back navigateur) sans être bloqué.
  // allowDismissRef = true bypass le blocage (signin réussi).
  // Logue chaque blocage pour identifier la source.
  useEffect(() => {
    if (Platform.OS !== 'web') return undefined;
    const unsub = navigation.addListener('beforeRemove', (e) => {
      if (allowDismissRef.current) return; // login réussi
      const elapsed = Date.now() - lastAttemptRef.current;
      if (lastAttemptRef.current > 0 && elapsed < 1500) {
        console.warn('[Login] beforeRemove BLOQUE (dans 1500ms post-signin) :',
          e?.data?.action?.type, 'elapsed=', elapsed, 'ms');
        e.preventDefault();
      }
    });
    return unsub;
  }, [navigation]);

  /* ── Google ──────────────────────────────────────────────── */
  const onGoogle = async () => {
    setBusy('google');
    try {
      if (!IS_AWS_CONFIGURED) {
        // Mode démo — connexion simulée
        await new Promise(r => setTimeout(r, 1200));
        await signInWithGoogle({
          name: 'Sofia Dupont', email: 'sofia.dupont@gmail.com', picture: null,
        });
        return;
      }

      // ── Mode production : Cognito Hosted UI ──────────────────
      const domain      = AWS_CONFIG.oauth?.domain;
      const clientId    = AWS_CONFIG.userPoolClientId;
      const redirectUri = getRedirectUri();
      console.log('[Login Google] redirectUri =', redirectUri);

      if (!domain) {
        Alert.alert(
          t('config_missing_title'),
          t('config_missing_msg')
        );
        return;
      }

      // v1.12.488 — login_hint pour pré-sélectionner le compte Google de l'user
      // (évite authuser=2 → 400 quand le navigateur a plusieurs comptes Google).
      // L'email est stocké au précédent login dans localStorage (clé last_google_email).
      let loginHint = '';
      // v1.12.800 — On retire le login_hint côté web : Google saute le sélecteur
      // quand un hint email est fourni, même avec prompt=select_account.
      // Sur mobile on garde le hint (UX différente : utile pour pré-remplir).
      if (Platform.OS !== 'web') {
        try {
          if (typeof window !== 'undefined' && window.localStorage) {
            const last = window.localStorage.getItem('last_google_email');
            if (last && /@/.test(last)) loginHint = '&login_hint=' + encodeURIComponent(last);
          }
        } catch {}
      }

      // v1.12.938 — Langue passée à Google/Cognito (la page accounts.google.com
      // s'ouvrait en FR au lieu de la langue de l'app). 'fr-FR' → 'fr'.
      const googleHl = (lang || 'en').split('-')[0].toLowerCase();
      // Build Cognito Hosted UI URL with Google as identity provider
      const params = 'response_type=code' +
        '&client_id=' + encodeURIComponent(clientId) +
        '&redirect_uri=' + encodeURIComponent(redirectUri) +
        '&scope=email%20openid%20profile' +
        '&identity_provider=Google' +
        '&prompt=select_account' +
        '&lang=' + googleHl +
        loginHint;
      const authUrl = 'https://' + domain + '/oauth2/authorize?' + params;

      // v1.12.800 — Force l'account selector Google côté web via pixel logout.
      // Cognito ne forward pas prompt=select_account à Google → si l'user est
      // déjà connecté à Google, redirect direct sans sélecteur. On expire le
      // cookie session Google via une image GET vers /Logout (bypass CSP, pas
      // de full redirect). Puis on redirige vers Cognito après 800ms.
      if (Platform.OS === 'web' && typeof window !== 'undefined') {
        try {
          const img = new window.Image();
          img.referrerPolicy = 'no-referrer';
          img.src = 'https://accounts.google.com/Logout?hl=' + googleHl + '&_=' + Date.now();
        } catch {}
        // Délai pour laisser le pixel logout charger avant le redirect
        setTimeout(() => { window.location.href = authUrl; }, 800);
        return;
      }

      // ═══ v1.12.120 — Fix "another web browser is already open" ═══
      // Si une session OAuth précédente n'a pas été correctement fermée
      // (crash, navigation brutale, multi-tap), iOS/Android refuse d'en
      // ouvrir une nouvelle. On force le dismiss + cooldown avant.
      try { await WebBrowser.dismissAuthSession(); } catch {}
      try { await WebBrowser.dismissBrowser(); } catch {}
      // maybeCompleteAuthSession nettoie les anciennes sessions en attente
      try { WebBrowser.maybeCompleteAuthSession(); } catch {}

      // ═══ Session NON-éphémère = comptes Google déjà connectés visibles ═══
      // preferEphemeralSession: false (default) → SFAuthenticationSession
      // partage les cookies avec Safari (iOS) / Chrome (Android). Du coup :
      //   - L'utilisateur voit directement la liste de ses comptes Google
      //     déjà connectés sur l'appareil — un seul tap pour se connecter,
      //     plus besoin de retaper le mot de passe à chaque ouverture.
      //   - prompt=select_account assure quand même l'affichage du sélecteur
      //     même quand un seul compte est connu (utile pour changer de compte).
      // Le signOut côté Cognito (/logout endpoint) est déjà appelé dans
      // AuthContext.signOut → la session est bien fermée à la déconnexion.
      let result;
      try {
        result = await WebBrowser.openAuthSessionAsync(
          authUrl,
          redirectUri,
          { preferEphemeralSession: false }
        );
      } catch (browserErr) {
        const msg = String(browserErr?.message || '');
        // Si erreur "another web browser is already open" → 2nde tentative
        // après dismiss agressif. Souvent un soft-retry suffit.
        if (msg.toLowerCase().includes('already open')) {
          console.warn('[Login Google] retry after dismiss:', msg);
          try { await WebBrowser.dismissAuthSession(); } catch {}
          try { await WebBrowser.dismissBrowser(); } catch {}
          await new Promise(r => setTimeout(r, 400));
          result = await WebBrowser.openAuthSessionAsync(
            authUrl,
            redirectUri,
            { preferEphemeralSession: false }
          );
        } else {
          throw browserErr;
        }
      }

      if (result.type === 'success' && result.url) {
        // Extract authorization code
        const url    = new URL(result.url);
        const code   = url.searchParams.get('code');
        const errMsg = url.searchParams.get('error_description');

        if (!code) {
          throw new Error(errMsg || 'No authorization code returned');
        }

        // Exchange code for tokens
        const tokenRes = await fetch(`https://${domain}/oauth2/token`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
          body: 'grant_type=authorization_code' +
            '&client_id=' + encodeURIComponent(clientId) +
            '&code=' + encodeURIComponent(code) +
            '&redirect_uri=' + encodeURIComponent(redirectUri),
        });
        const tokens = await tokenRes.json();
        if (tokens.error) throw new Error(tokens.error_description || tokens.error);

        // Decode id_token first
        const idPayload = decodeJwt(tokens.id_token);

        // Call /oauth2/userInfo to get full Google profile (name, picture, etc.)
        let userInfo = {};
        try {
          const uiRes = await fetch(`https://${domain}/oauth2/userInfo`, {
            headers: { Authorization: `Bearer ${tokens.access_token}` },
          });
          if (uiRes.ok) userInfo = await uiRes.json();
        } catch (e) {
          console.warn('userInfo fetch failed:', e.message);
        }

        // Merge: userInfo has the real Google data
        const name       = userInfo.name        || idPayload.name        || idPayload.email || '';
        const email      = userInfo.email        || idPayload.email        || '';
        const picture    = userInfo.picture      || idPayload.picture      || null;
        const firstName  = userInfo.given_name   || idPayload.given_name   || name.split(' ')[0] || '';
        const lastName   = userInfo.family_name  || idPayload.family_name  || name.split(' ').slice(1).join(' ') || '';

        // Sign in and save to DB
        await signInWithGoogle({
          accessToken:  tokens.access_token,
          idToken:      tokens.id_token,
          refreshToken: tokens.refresh_token,
          name,
          email,
          picture,
          firstName,
          lastName,
        });
        // v1.12.121 — Reset stack vers Main sur natif (sinon l'user reste
        // sur le modal Login). Sur web, le useEffect (cf bootDone) gère.
        if (Platform.OS !== 'web') {
          try {
            navigation.dispatch(StackActions.popToTop());
            setTimeout(() => {
              try { rootReplace('Main'); } catch {}
            }, 50);
          } catch {}
        }
      } else if (result.type === 'cancel') {
        // User cancelled — silent
      }
    } catch (e) {
      Alert.alert(t('signin_failed_title'), e.message);
    } finally {
      setBusy(null);
    }
  };

  /* ── Apple ───────────────────────────────────────────────── */
  // Apple Sign-In natif : iOS 13+ uniquement (obligatoire App Store Review
  // si d'autres providers tiers sont proposés). Sur Android, on cache le
  // bouton (Apple fournit un flux web mais inutile pour notre cas).
  const onApple = async () => {
    setBusy('apple');
    try {
      if (!IS_AWS_CONFIGURED) {
        // Demo mode : faux credential
        await new Promise(r => setTimeout(r, 800));
        await signInWithApple({
          fullName: { givenName: 'Sofia', familyName: 'A.' },
          email: 'sofia.demo@privaterelay.appleid.com',
          user: 'apple_demo_001',
          identityToken: 'demo.token',
        });
        return;
      }

      if (Platform.OS !== 'ios') {
        Alert.alert(
          t('apple_unavailable_title'),
          t('apple_unavailable_msg')
        );
        return;
      }

      // Import dynamique : le module n'est dispo qu'en dev-build/prod-build
      // (pas en Expo Go). require() protégé par try/catch pour éviter crash.
      let AppleAuth;
      try {
        AppleAuth = require('expo-apple-authentication');
      } catch (e) {
        Alert.alert(
          t('apple_module_missing_title'),
          t('apple_module_missing_msg')
        );
        return;
      }

      const available = await AppleAuth.isAvailableAsync();
      if (!available) {
        Alert.alert(t('apple_unavailable_title'), t('apple_ios13_required'));
        return;
      }

      let cred;
      try {
        cred = await AppleAuth.signInAsync({
          requestedScopes: [
            AppleAuth.AppleAuthenticationScope.FULL_NAME,
            AppleAuth.AppleAuthenticationScope.EMAIL,
          ],
        });
      } catch (appleErr) {
        // User a annulé → silencieux, pas d'alerte
        if (appleErr.code === 'ERR_REQUEST_CANCELED' ||
            appleErr.code === 'ERR_CANCELED') {
          return;
        }
        throw appleErr;
      }

      // Transmettre le credential complet au context
      // → AuthContext.signInWithApple s'occupe d'appeler notre Lambda /auth/apple
      const result = await signInWithApple(cred);
      if (!result?.success) {
        Alert.alert(
          t('apple_signin_failed_title'),
          result?.error || t('apple_signin_unknown_error')
        );
        return;
      }
      // v1.12.121 — Reset stack vers Main sur natif (cf onGoogle)
      if (Platform.OS !== 'web') {
        try {
          navigation.dispatch(StackActions.popToTop());
          setTimeout(() => {
            try { rootReplace('Main'); } catch {}
          }, 50);
        } catch {}
      }
    } catch (e) {
      console.error('[LoginScreen] Apple error:', e);
      Alert.alert(t('apple_signin_failed_title'), e.message || t('apple_signin_unknown_error'));
    } finally {
      setBusy(null);
    }
  };

  const isLoading = loading || !!busy;

  // v1.12.268 — Panel debug pour Safari mobile (visible si ?debug=1 dans URL ou tap 7× sur le logo)
  const [showDebug, setShowDebug] = useState(false);
  const [logoTaps, setLogoTaps] = useState(0);
  const [debugLogs, setDebugLogs] = useState('');
  React.useEffect(() => {
    if (typeof window !== 'undefined' && window.location?.search?.includes('debug=1')) {
      setShowDebug(true);
    }
  }, []);
  const refreshDebugLogs = () => {
    try {
      const raw = localStorage.getItem('mt_signin_debug') || '[]';
      const arr = JSON.parse(raw);
      setDebugLogs(arr.map(l => `[${l.t.slice(11, 19)}] ${l.msg}: ${JSON.stringify({ ...l, t: undefined, msg: undefined })}`).join('\n'));
    } catch (e) { setDebugLogs('Error: ' + e.message); }
  };
  const onLogoTap = () => {
    const next = logoTaps + 1;
    setLogoTaps(next);
    if (next >= 7) { setShowDebug(true); refreshDebugLogs(); setLogoTaps(0); }
  };

  const insets = useSafeAreaInsets();
  return (
    <SafeAreaView style={s.safe}>
      <KeyboardAvoidingView
        style={{ flex: 1 }}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
      <ScrollView
        contentContainerStyle={[s.scroll, Platform.OS !== 'web' && { paddingBottom: spacing.xl + insets.bottom + 24 }]}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        automaticallyAdjustKeyboardInsets={Platform.OS === 'ios'}
      >
        {/* v1.12.853 — Logo Mytenue Gold (mytenue logo_Gold.png) sur fond clair */}
        <Pressable style={s.logoBlock} onPress={onLogoTap}>
          <Image
            source={require('../../assets/logo-login-gold.png')}
            style={s.logoGold}
            resizeMode="contain"
          />
          <View style={s.logoDivider} />
          <Text style={s.logoTagline}>{t('auth_tagline')}</Text>
        </Pressable>

        {/* v1.12.268 — Debug panel (tap 7× sur logo OU ?debug=1) */}
        {showDebug && (
          <View style={{ margin: 16, padding: 12, backgroundColor: '#1A1814', borderRadius: 8 }}>
            <View style={{ flexDirection: 'row', justifyContent: 'space-between', marginBottom: 8 }}>
              <Text style={{ color: '#D4AF72', fontSize: 11, fontWeight: '700' }}>🔍 DEBUG SIGNIN LOGS</Text>
              <Pressable onPress={refreshDebugLogs}><Text style={{ color: '#D4AF72', fontSize: 11 }}>↻ refresh</Text></Pressable>
            </View>
            <Text selectable style={{ color: '#FFF', fontSize: 9, fontFamily: 'monospace', lineHeight: 13 }}>
              {debugLogs || '(no logs yet — try signing in)'}
            </Text>
            <Pressable onPress={() => { localStorage.removeItem('mt_signin_debug'); setDebugLogs(''); }} style={{ marginTop: 8 }}>
              <Text style={{ color: '#D4AF72', fontSize: 10, textAlign: 'center' }}>Clear logs</Text>
            </Pressable>
          </View>
        )}

        {/* Features */}
        <View style={s.features}>
          {[
            { icon: '✦', title: t('feature_smart_wardrobe_title'), desc: t('feature_smart_wardrobe_desc') },
            { icon: '✦', title: t('feature_ai_looks_title'),       desc: t('feature_ai_looks_desc') },
            { icon: '✦', title: t('feature_tryon_flatlay_title'),  desc: t('feature_tryon_flatlay_desc') },
          ].map((f, i, arr) => (
            <View key={i} style={[s.featureRow, i < arr.length - 1 && s.featureRowBorder]}>
              <Text style={s.featureDot}>{f.icon}</Text>
              <View style={s.featureText}>
                <Text style={s.featureTitle}>{f.title}</Text>
                <Text style={s.featureDesc}>{f.desc}</Text>
              </View>
            </View>
          ))}
        </View>

        {/* Auth */}
        <View style={s.authSection}>
          <Text style={s.authLabel}>{t('auth_section_label')}</Text>

          {/* Apple — iOS uniquement (Sign In with Apple natif).
              Sur Web/Android, on cache le bouton car expo-apple-authentication
              n'existe pas et le flux web Apple JS demanderait une config
              séparée. v1.12.116 — Apple supprimé sur Web sur demande user. */}
          {Platform.OS === 'ios' && (
            <TouchableOpacity
              style={[s.btn, s.btnApple, isLoading && s.btnDisabled]}
              onPress={onApple} disabled={isLoading} activeOpacity={0.85}
            >
              {busy === 'apple'
                ? <ActivityIndicator color="#fff" size="small" />
                : <>
                    <Text style={s.appleIcon}></Text>
                    <Text style={s.btnAppleText}>{t('continue_with_apple')}</Text>
                  </>
              }
            </TouchableOpacity>
          )}

          {/* Google */}
          <TouchableOpacity
            style={[s.btn, s.btnGoogle, isLoading && s.btnDisabled]}
            onPress={onGoogle} disabled={isLoading} activeOpacity={0.85}
          >
            {busy === 'google'
              ? <ActivityIndicator color={colors.text.primary} size="small" />
              : <>
                  <View style={s.googleIcon}>
                    <Text style={s.googleG}>G</Text>
                  </View>
                  <Text style={s.btnGoogleText}>{t('continue_with_google')}</Text>
                </>
            }
          </TouchableOpacity>

          {/* Séparateur "ou" — v1.12.117 */}
          <View style={s.dividerRow}>
            <View style={s.dividerLine} />
            <Text style={s.dividerText}>{t('divider_or')}</Text>
            <View style={s.dividerLine} />
          </View>

          {/* Email + password — v1.12.118 : champs rouges si erreur */}
          <TextInput
            value={emailField}
            onChangeText={onEmailChange}
            placeholder={t('email_label')}
            keyboardType="email-address"
            autoCapitalize="none"
            autoCorrect={false}
            textContentType="emailAddress"
            placeholderTextColor={colors.text.ghost}
            style={[s.fieldInput, loginError && s.fieldInputError]}
            editable={!isLoading}
          />
          <View style={{ position: 'relative' }}>
            <TextInput
              value={passwordField}
              onChangeText={onPasswordChange}
              placeholder={t('password')}
              secureTextEntry={!showPwd}
              autoCapitalize="none"
              autoCorrect={false}
              textContentType="password"
              placeholderTextColor={colors.text.ghost}
              style={[s.fieldInput, { paddingRight: 70 }, loginError && s.fieldInputError]}
              editable={!isLoading}
              onSubmitEditing={onEmailLogin}
              returnKeyType="go"
            />
            <TouchableOpacity
              onPress={() => setShowPwd(v => !v)}
              style={s.eyeBtn}
              hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
            >
              <Text style={s.eyeBtnText}>{showPwd ? t('hide_password') : t('show_password')}</Text>
            </TouchableOpacity>
          </View>
          {/* Message d'erreur inline */}
          {loginError === 'invalid' && (
            <Text style={s.errorText}>{t('err_invalid_credentials')}</Text>
          )}
          {loginError === 'empty' && (
            <Text style={s.errorText}>{t('err_empty_credentials')}</Text>
          )}
          {loginError === 'invalid_email' && (
            <Text style={s.errorText}>{t('err_invalid_email_format')}</Text>
          )}
          {loginError === 'not_confirmed' && (
            <Text style={s.errorText}>{t('err_account_not_confirmed')}</Text>
          )}

          <TouchableOpacity
            style={[s.btn, s.btnEmail, isLoading && s.btnDisabled]}
            onPress={onEmailLogin}
            disabled={isLoading}
            activeOpacity={0.85}
          >
            {busy === 'email'
              ? <ActivityIndicator color="#fff" size="small" />
              : <Text style={s.btnEmailText}>{t('sign_in_cta')}</Text>}
          </TouchableOpacity>

          <TouchableOpacity onPress={onForgotPassword} style={s.forgotBtn}>
            <Text style={s.forgotText}>{t('forgot_password_q')}</Text>
          </TouchableOpacity>

          <TouchableOpacity
            onPress={() => navigation.navigate('SignUp')}
            disabled={isLoading}
            style={s.signUpBtn}
            activeOpacity={0.85}
          >
            <Text style={s.signUpText}>
              {t('no_account_yet')} <Text style={s.signUpTextStrong}>{t('create_account_link')}</Text>
            </Text>
          </TouchableOpacity>
        </View>

        {/* ═══ Acceptation légale ═══ */}
        {/* Phrase d'intro non cliquable (pas de Text imbriqué qui ne marche
            pas toujours sur iOS), puis 2 vrais boutons explicites en dessous
            qui ouvrent les écrans Terms et Privacy. */}
        <Text style={s.legal}>{t('legal_acceptance_text')}</Text>
        <View style={s.legalLinks}>
          <Pressable
            onPress={() => openLegalUrl('terms', lang)}
            hitSlop={{ top: 16, bottom: 16, left: 16, right: 16 }}
            style={({ pressed }) => [s.legalLinkBtn, pressed && { opacity: 0.5 }]}
          >
            <Text style={s.legalLinkText}>{t('legal_terms_link')}</Text>
          </Pressable>
          <Text style={s.legalLinkSep}>·</Text>
          <Pressable
            onPress={() => openLegalUrl('privacy', lang)}
            hitSlop={{ top: 16, bottom: 16, left: 16, right: 16 }}
            style={({ pressed }) => [s.legalLinkBtn, pressed && { opacity: 0.5 }]}
          >
            <Text style={s.legalLinkText}>{t('legal_privacy_link')}</Text>
          </Pressable>
        </View>
        {/* v1.12.153 — éditeur visible (TimeOne / réseaux affiliation) */}
        <Text style={[s.legalLinkText, { textAlign: 'center', marginTop: 14, opacity: 0.85, textDecorationLine: 'none', fontSize: 11 }]}>
          {t('legal_publisher')}
        </Text>
        <Text style={[s.legalLinkText, { textAlign: 'center', marginTop: 4, opacity: 0.7, textDecorationLine: 'none', fontSize: 10 }]}>
          {t('legal_siret')}
        </Text>
      </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

function decodeJwt(token) {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4 ? b64 + '='.repeat(4 - b64.length % 4) : b64;
    return JSON.parse(atob(pad));
  } catch { return {}; }
}

const s = StyleSheet.create({
  safe:             { flex: 1, backgroundColor: colors.bg.base },
  // v1.12.117 — login compact : tout tient sur 1 viewport (pas de scroll).
  // justifyContent center fait que le bloc est vertically centered, pas en haut.
  scroll:           Platform.select({
    web: {
      flexGrow: 1,
      paddingHorizontal: spacing.xl,
      paddingTop: 24,
      paddingBottom: 24,
      maxWidth: 440,
      width: '100%',
      alignSelf: 'center',
      justifyContent: 'center',
    },
    default: {
      flexGrow: 1,
      paddingHorizontal: spacing.xl,
      paddingTop: 28,
      paddingBottom: 28,
      justifyContent: 'center',
    },
  }),

  logoBlock:        { alignItems: 'center', marginBottom: 18 },
  // v1.12.853 — Logo Gold image (PNG) à la place du SVG cursive
  // v1.12.928 — Réduit à 280×108 pour cohérence visuelle avec logo email (240px)
  logoGold:         { width: 280, height: 108, marginBottom: 10, resizeMode: 'contain' },
  logoCircle:       { width: 72, height: 72, borderRadius: 36, borderWidth: 1.5, borderColor: colors.text.primary, alignItems: 'center', justifyContent: 'center', marginBottom: 16 },
  logoO:            { fontSize: 30, fontWeight: '200', color: colors.text.primary, letterSpacing: 2 },
  logoWord:         { fontSize: 26, fontWeight: '200', letterSpacing: 10, color: colors.text.primary, marginBottom: 8 },
  logoDivider:      { width: 32, height: 1, backgroundColor: colors.bg.border, marginBottom: 8 },
  logoTagline:      { fontSize: 11, letterSpacing: 3, color: colors.text.faint, textTransform: 'uppercase' },

  features:         { backgroundColor: colors.bg.surface, borderRadius: radius.lg, borderWidth: 1, borderColor: colors.bg.border, marginBottom: 18, overflow: 'hidden' },
  featureRow:       { flexDirection: 'row', alignItems: 'flex-start', gap: 10, paddingVertical: 10, paddingHorizontal: spacing.md },
  featureRowBorder: { borderBottomWidth: 1, borderBottomColor: colors.bg.border },
  featureDot:       { fontSize: 10, color: colors.accent.main, marginTop: 2, width: 14 },
  featureText:      { flex: 1 },
  featureTitle:     { fontSize: 13, fontWeight: '600', color: colors.text.primary, marginBottom: 1 },
  featureDesc:      { fontSize: 11, color: colors.text.faint, lineHeight: 14 },

  authSection:      { gap: 10, marginBottom: 14 },
  authLabel:        { fontSize: 10, letterSpacing: 3, color: colors.text.faint, textTransform: 'uppercase', textAlign: 'center', marginBottom: 2 },

  btn:              { height: 48, borderRadius: radius.md, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10, ...shadow.sm },
  btnDisabled:      { opacity: 0.5 },

  btnApple:         { backgroundColor: '#000000' },
  appleIcon:        { fontSize: 20, color: '#fff', lineHeight: 24 },
  btnAppleText:     { fontSize: 15, fontWeight: '500', color: '#fff' },

  btnGoogle:        { backgroundColor: colors.bg.base, borderWidth: 1.5, borderColor: colors.bg.border },
  googleIcon:       { width: 24, height: 24, borderRadius: 12, backgroundColor: '#fff', borderWidth: 1, borderColor: '#E8E4DC', alignItems: 'center', justifyContent: 'center' },
  googleG:          { fontSize: 13, fontWeight: '700', color: '#4285F4' },
  btnGoogleText:    { fontSize: 15, fontWeight: '500', color: colors.text.primary },

  // ═══ v1.12.117 — séparateur OU + email/password ═══
  dividerRow:       { flexDirection: 'row', alignItems: 'center', gap: 10, marginVertical: 6 },
  dividerLine:      { flex: 1, height: 1, backgroundColor: colors.bg.border },
  dividerText:      { fontSize: 10, letterSpacing: 2, color: colors.text.ghost, fontWeight: '600' },
  fieldInput: {
    height: 46,
    borderRadius: radius.md,
    backgroundColor: colors.bg.surface,
    borderWidth: 1,
    borderColor: colors.bg.border,
    paddingHorizontal: 14,
    fontSize: 14,
    color: colors.text.primary,
  },
  // v1.12.118 — champ en erreur (login invalide)
  fieldInputError: {
    borderColor: '#C62828',
    borderWidth: 1.5,
    backgroundColor: '#FFF5F5',
  },
  errorText: {
    fontSize: 12,
    color: '#C62828',
    marginTop: -4,
    marginLeft: 4,
    marginBottom: 2,
    fontWeight: '500',
  },
  eyeBtn:           { position: 'absolute', right: 12, top: 0, bottom: 0, justifyContent: 'center' },
  eyeBtnText:       { fontSize: 11, fontWeight: '600', letterSpacing: 0.4, color: colors.text.faint },
  btnEmail:         { backgroundColor: '#9B7B5A' },
  btnEmailText:     { fontSize: 14, fontWeight: '700', color: '#fff', letterSpacing: 1 },
  forgotBtn:        { alignSelf: 'center', paddingVertical: 4, marginTop: -2 },
  forgotText:       { fontSize: 12, color: colors.text.faint, textDecorationLine: 'underline' },
  signUpBtn:        { alignItems: 'center', paddingVertical: 8, marginTop: 4 },
  signUpText:       { fontSize: 13, color: colors.text.faint },
  signUpTextStrong: { color: '#9B7B5A', fontWeight: '700' },

  statusBadge:      { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 7, backgroundColor: colors.bg.surface, borderRadius: radius.sm, borderWidth: 1, borderColor: colors.bg.border, paddingVertical: 10, paddingHorizontal: spacing.md },
  statusDot:        { width: 7, height: 7, borderRadius: 4 },
  statusDotLive:    { backgroundColor: colors.status.success },
  statusDotDemo:    { backgroundColor: colors.accent.light },
  statusText:       { fontSize: 11, color: colors.text.faint, textAlign: 'center' },

  legal:            { fontSize: 11, color: colors.text.ghost, textAlign: 'center', lineHeight: 16, marginBottom: 4 },
  legalLink:        { color: colors.text.faint, textDecorationLine: 'underline' },
  legalLinks:       { flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: 10, marginTop: 8, flexWrap: 'wrap' },
  legalLinkBtn:     { paddingVertical: 10, paddingHorizontal: 8, minHeight: 44, justifyContent: 'center' },
  legalLinkText:    { fontSize: 12, color: colors.text.muted, textDecorationLine: 'underline', letterSpacing: 0.2, fontWeight: '500' },
  legalLinkSep:     { fontSize: 12, color: colors.text.ghost },
});
